import {
  Authorizations,
  AuthorizationPermissions,
  AuthorizationResourcesAndSubjects
} from '@/types/authorization'

export const PlatformSuperAdmin: Authorizations = [
  {
    permission: AuthorizationPermissions.IsSuperAdministrator,
    resourceType: AuthorizationResourcesAndSubjects.Platform
  },
  {
    permission: AuthorizationPermissions.SuperAdminstratorAccessing,
    resourceType: AuthorizationResourcesAndSubjects.Platform
  }
]

/**
 * Gestão › Afiliados e "ver como": `can_view_antecipe_affiliates` (super admin +
 * antecipe_affiliate_viewer no SpiceDB). Mantém o super admin explícito para valer
 * também antes do schema novo chegar ao ambiente.
 */
export const AntecipeAffiliatesViewer: Authorizations = [
  ...PlatformSuperAdmin,
  {
    permission: AuthorizationPermissions.CanViewAntecipeAffiliates,
    resourceType: AuthorizationResourcesAndSubjects.Platform
  }
]

export const CompanyGeneralAdmin: Authorizations = [
  {
    permission: AuthorizationPermissions.AccessLevelEditing,
    resourceType: AuthorizationResourcesAndSubjects.Company
  },
  {
    permission: AuthorizationPermissions.AccessLevelGranting,
    resourceType: AuthorizationResourcesAndSubjects.Company
  },
  {
    permission: AuthorizationPermissions.AccessLevelRemoving,
    resourceType: AuthorizationResourcesAndSubjects.Company
  }
]

export const CompanyContentAdmin: Authorizations = [
  {
    permission: AuthorizationPermissions.SharingModalReading,
    resourceType: AuthorizationResourcesAndSubjects.Company
  },
  {
    permission: AuthorizationPermissions.CompanyPolicyLinksEditingGranted,
    resourceType: AuthorizationResourcesAndSubjects.Company
  }
]

export const HealthUnitContentAdmin: Authorizations = [
  {
    permission: AuthorizationPermissions.HealthUnitPolicyLinksEditing,
    resourceType: AuthorizationResourcesAndSubjects.HealthUnit
  },
  {
    permission: AuthorizationPermissions.HealthUnitCreating,
    resourceType: AuthorizationResourcesAndSubjects.HealthUnit
  },
  {
    permission: AuthorizationPermissions.HealthUnitEditing,
    resourceType: AuthorizationResourcesAndSubjects.HealthUnit
  },
  {
    permission: AuthorizationPermissions.HealthUnitDeactivating,
    resourceType: AuthorizationResourcesAndSubjects.HealthUnit
  }
]

// Edicao da unidade: gate da fila "Revisao da Geolocalizacao (pino)" e das acoes
// de pino (aplicar sugestao, descartar) na faixa do detalhe.
export const HealthUnitEditor: Authorizations = [
  {
    permission: AuthorizationPermissions.HealthUnitEditing,
    resourceType: AuthorizationResourcesAndSubjects.HealthUnit
  }
]

export const HealthUnitBasicReader: Authorizations = [
  {
    permission: AuthorizationPermissions.HealthUnitBasicReading,
    resourceType: AuthorizationResourcesAndSubjects.HealthUnit
  }
]

export const ScheduleContentAdmin: Authorizations = [
  {
    permission: AuthorizationPermissions.SchedulePolicyLinksEditing,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.SchedulePaymentPoliciesCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.SchedulePaymentPoliciesEditing,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.SchedulePaymentPoliciesDeactivating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.ScheduleFormationCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.ScheduleFormationEditing,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.ScheduleFormationDeactivating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.SchedulePositionCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.SchedulePositionEditing,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.SchedulePositionDeactivating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.ScheduleShiftCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.ScheduleShiftEditing,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.ScheduleShiftDeactivating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.ScheduleCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.ScheduleEditing,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.ScheduleDeactivating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  }
]

/**
 * Comunicados (PoC Novo Hamburgo, V-b): o backend exige `station_editing` (papel
 * basic_editor ou superior) em todas as escalas do comunicado; o menu/rota pedem a
 * mesma permissão em alguma escala.
 */
export const AnnouncementManager: Authorizations = [
  {
    permission: AuthorizationPermissions.StationEditing,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  }
]

/**
 * Termos de ciência (PRD26): o termo é global, então publica e consulta o super admin
 * ou o administrador geral de qualquer empresa (`company_user_creating`, decisão G25),
 * a mesma regra do `TermsEditorGuard` do backend.
 */
export const TermsManager: Authorizations = [
  ...PlatformSuperAdmin,
  {
    permission: AuthorizationPermissions.CompanyUserCreating,
    resourceType: AuthorizationResourcesAndSubjects.Company
  }
]

export const ScheduleAdvancedReader: Authorizations = [
  {
    permission: AuthorizationPermissions.StationExceptionalitiesListReading,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.SchedulePaymentPoliciesLinksReading,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.SchedulePaymentPoliciesReading,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.ScheduleProportionalityRuleReading,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.ScheduleRequerimentReading,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  }
]

// Leitura das paginas de Politicas (Pos-Plantao): concede acesso aos Analistas
// (documentacao/financeiro) alem do Leitor Avancado de escala. Combinado via spread
// aditivo com ScheduleAdvancedReader nas rotas /politicas/* (semantica OR no guard).
/**
 * Gestão (`/gestao`): a tela abre nos relatórios mensal e anual, que o backend
 * libera com `station_doctor_check_xls_report_exporting` na escala.
 */
export const ManagementReader: Authorizations = [
  {
    permission: AuthorizationPermissions.StationDoctorCheckXlsReportExporting,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  }
]

export const PostStationPolicies: Authorizations = [
  {
    permission: AuthorizationPermissions.QualificationDocumentalPolicyReading,
    resourceType: AuthorizationResourcesAndSubjects.PostStationCompany
  },
  {
    permission: AuthorizationPermissions.QualificationTransferPolicyReading,
    resourceType: AuthorizationResourcesAndSubjects.PostStationCompany
  },
  {
    permission: AuthorizationPermissions.QualificationDocumentalPolicyReading,
    resourceType: AuthorizationResourcesAndSubjects.PostStationHealthUnit
  },
  {
    permission: AuthorizationPermissions.QualificationTransferPolicyReading,
    resourceType: AuthorizationResourcesAndSubjects.PostStationHealthUnit
  }
]

// Leitura do Painel Pos-Plantao: acesso via grant de post_station_panel_reading
// no escopo Company ou HealthUnit (semantica OR no guard).
export const PostStationPanelReader: Authorizations = [
  {
    permission: AuthorizationPermissions.PostStationPanelReading,
    resourceType: AuthorizationResourcesAndSubjects.PostStationCompany
  },
  {
    permission: AuthorizationPermissions.PostStationPanelReading,
    resourceType: AuthorizationResourcesAndSubjects.PostStationHealthUnit
  }
]

// Backoffice do recalculo do horario efetivo. A permissao e a da F1 §6.2
// (`attendance_general_administrating`), e nao o `effective_check_recalculating`
// que o mockup M5 escreveu; o spec F2-F4 §7.5 e explicito sobre isso.
// Company OU HealthUnit (semantica OR no guard).
export const EffectiveCheckRecalculator: Authorizations = [
  {
    permission: AuthorizationPermissions.AttendanceGeneralAdministrating,
    resourceType: AuthorizationResourcesAndSubjects.DsAttendanceCompany
  },
  {
    permission: AuthorizationPermissions.AttendanceGeneralAdministrating,
    resourceType: AuthorizationResourcesAndSubjects.DsAttendanceHealthUnit
  }
]

export const ScheduleBasicReader: Authorizations = [
  {
    permission: AuthorizationPermissions.ScheduleBasicReading,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  }
]

export const StationContentAdmin: Authorizations = [
  {
    permission: AuthorizationPermissions.StationExceptionalitiesReverting,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.StationExceptionalitiesApproving,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.StationExceptionalitiesReproving,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.StationObservationsDeleting,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.StationObservationsEditing,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.StationDeleting,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  }
]

export const StationIntermediateEditor: Authorizations = [
  {
    permission: AuthorizationPermissions.StationChecksEvidencesManaging,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.StationCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  }
]

export const StationBasicEditor: Authorizations = [
  {
    permission: AuthorizationPermissions.StationDoctorPackageRemoving,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.StationDoctorPackageReplacing,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.StationDoctorPackageAllocating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.StationExceptionalitiesCancelling,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.StationExceptionalitiesRequesting,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.StationObservationsCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.StationEditing,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  }
]

export const StationAdvancedReader: Authorizations = [
  {
    permission: AuthorizationPermissions.StationExceptionalitiesReading,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.StationObservationsReading,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.StationAntecipeReading,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.StationPaymentRulesReading,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  },
  {
    permission: AuthorizationPermissions.StationEditionHistoricalReading,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  }
]

// Perfil Extrator do Registro de Plantão: `attendance_reading` (emissor+ ∪ extrator)
// em qualquer um dos três escopos do ds_attendance. O extrator é ortogonal à Gestão
// de Escalas — sem isto a guarda o barrava antes de a página carregar, mesmo com o
// backend liberando a leitura.
export const DsAttendanceReader: Authorizations = [
  {
    permission: AuthorizationPermissions.AttendanceReading,
    resourceType: AuthorizationResourcesAndSubjects.DsAttendance
  },
  {
    permission: AuthorizationPermissions.AttendanceReading,
    resourceType: AuthorizationResourcesAndSubjects.DsAttendanceHealthUnit
  },
  {
    permission: AuthorizationPermissions.AttendanceReading,
    resourceType: AuthorizationResourcesAndSubjects.DsAttendanceCompany
  }
]

// Hub de aprovações e fila/detalhe do Registro de Plantão: leitor avançado de escala
// OU leitor do ds_attendance (semântica OR no guard).
export const DsAttendanceQueueReader: Authorizations = [
  ...StationAdvancedReader,
  ...DsAttendanceReader
]

export const StationIntermediateReader: Authorizations = [
  {
    permission: AuthorizationPermissions.StationDoctorCheckReading,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  }
]

export const StationBasicReader: Authorizations = [
  {
    permission: AuthorizationPermissions.StationBasicReading,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  }
]

export const NpsAdmin: Authorizations = [
  {
    permission: AuthorizationPermissions.NpsFormCreating,
    resourceType: AuthorizationResourcesAndSubjects.NpsCompany
  },
  {
    permission: AuthorizationPermissions.NpsFormCreating,
    resourceType: AuthorizationResourcesAndSubjects.NpsHealthUnit
  },
  {
    permission: AuthorizationPermissions.NpsFormEditing,
    resourceType: AuthorizationResourcesAndSubjects.NpsCompany
  },
  {
    permission: AuthorizationPermissions.NpsFormEditing,
    resourceType: AuthorizationResourcesAndSubjects.NpsHealthUnit
  },
  {
    permission: AuthorizationPermissions.NpsFormCloning,
    resourceType: AuthorizationResourcesAndSubjects.NpsCompany
  },
  {
    permission: AuthorizationPermissions.NpsFormCloning,
    resourceType: AuthorizationResourcesAndSubjects.NpsHealthUnit
  },
  {
    permission: AuthorizationPermissions.NpsFormMoving,
    resourceType: AuthorizationResourcesAndSubjects.NpsCompany
  },
  {
    permission: AuthorizationPermissions.NpsFormMoving,
    resourceType: AuthorizationResourcesAndSubjects.NpsHealthUnit
  },
  {
    permission: AuthorizationPermissions.NpsEvaluatorManaging,
    resourceType: AuthorizationResourcesAndSubjects.NpsCompany
  },
  {
    permission: AuthorizationPermissions.NpsEvaluatorManaging,
    resourceType: AuthorizationResourcesAndSubjects.NpsHealthUnit
  },
  {
    permission: AuthorizationPermissions.NpsSettingsManaging,
    resourceType: AuthorizationResourcesAndSubjects.NpsCompany
  },
  {
    permission: AuthorizationPermissions.NpsSettingsManaging,
    resourceType: AuthorizationResourcesAndSubjects.NpsHealthUnit
  },
  {
    permission: AuthorizationPermissions.NpsAuditExporting,
    resourceType: AuthorizationResourcesAndSubjects.NpsCompany
  },
  {
    permission: AuthorizationPermissions.NpsAuditExporting,
    resourceType: AuthorizationResourcesAndSubjects.NpsHealthUnit
  }
]

export const NpsEvaluator: Authorizations = [
  {
    permission: AuthorizationPermissions.NpsTechnicalEvaluating,
    resourceType: AuthorizationResourcesAndSubjects.NpsCompany
  },
  {
    permission: AuthorizationPermissions.NpsTechnicalEvaluating,
    resourceType: AuthorizationResourcesAndSubjects.NpsHealthUnit
  },
  {
    permission: AuthorizationPermissions.NpsInstitutionalEvaluating,
    resourceType: AuthorizationResourcesAndSubjects.NpsCompany
  },
  {
    permission: AuthorizationPermissions.NpsInstitutionalEvaluating,
    resourceType: AuthorizationResourcesAndSubjects.NpsHealthUnit
  }
]

export const NpsReader: Authorizations = [
  {
    permission: AuthorizationPermissions.NpsDashboardReading,
    resourceType: AuthorizationResourcesAndSubjects.NpsCompany
  },
  {
    permission: AuthorizationPermissions.NpsDashboardReading,
    resourceType: AuthorizationResourcesAndSubjects.NpsHealthUnit
  },
  {
    permission: AuthorizationPermissions.NpsRankingExporting,
    resourceType: AuthorizationResourcesAndSubjects.NpsCompany
  },
  {
    permission: AuthorizationPermissions.NpsRankingExporting,
    resourceType: AuthorizationResourcesAndSubjects.NpsHealthUnit
  },
  {
    permission: AuthorizationPermissions.NpsPatientNpsReading,
    resourceType: AuthorizationResourcesAndSubjects.NpsCompany
  },
  {
    permission: AuthorizationPermissions.NpsPatientNpsReading,
    resourceType: AuthorizationResourcesAndSubjects.NpsHealthUnit
  },
  {
    permission: AuthorizationPermissions.NpsOwnHistoryReading,
    resourceType: AuthorizationResourcesAndSubjects.NpsCompany
  },
  {
    permission: AuthorizationPermissions.NpsOwnHistoryReading,
    resourceType: AuthorizationResourcesAndSubjects.NpsHealthUnit
  }
]

export const StationExceptionalitiesContentAdmin: Authorizations = [
  {
    permission: AuthorizationPermissions.StationExceptionalitiesFlowConfiguring,
    resourceType: AuthorizationResourcesAndSubjects.StationExceptionalitiesCompany
  },
  {
    permission: AuthorizationPermissions.StationExceptionalitiesFlowConfiguring,
    resourceType: AuthorizationResourcesAndSubjects.StationExceptionalitiesHealthUnit
  },
  {
    permission: AuthorizationPermissions.StationExceptionalitiesFlowConfiguring,
    resourceType: AuthorizationResourcesAndSubjects.StationExceptionalitiesSchedule
  }
]

// Documentos do médico (medical-docs-qualification, D112): grupo de permissão "Documentação",
// por empresa (`professional_documents_company`), sem relação com a Gestão de Escalas.
// Leitor: fila, detalhe, arquivo e Kanban em leitura (detalhe do médico, AC13).
export const DoctorDocumentReader: Authorizations = [
  {
    permission: AuthorizationPermissions.ProfessionalDocumentsReading,
    resourceType: AuthorizationResourcesAndSubjects.ProfessionalDocumentsCompany
  }
]

// Editor: aprovar, reprovar, lote, desfazer a própria decisão e mover card do Kanban.
export const DoctorDocumentReviewer: Authorizations = [
  {
    permission: AuthorizationPermissions.ProfessionalDocumentsReviewing,
    resourceType: AuthorizationResourcesAndSubjects.ProfessionalDocumentsCompany
  }
]

// Administrador: desfazer a decisão de outro revisor e editar os requisitos documentais da escala.
export const DoctorDocumentAdministrator: Authorizations = [
  {
    permission: AuthorizationPermissions.ProfessionalDocumentsAdministrating,
    resourceType: AuthorizationResourcesAndSubjects.ProfessionalDocumentsCompany
  }
]
